import React, { useEffect, useState, useRef } from 'react';
import { useAuth, apiGet, apiPut, apiUpload } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { User, Upload, CheckCircle, QrCode, X } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api';

const API = API_BASE_URL;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function ProfilePage() {
  const { user } = useAuth();
  const [guest, setGuest] = useState(null);
  const [rsvp, setRsvp] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    apiGet('/api/guests/me').then(r => {
      setGuest(r.data.guest);
      setRsvp(r.data.rsvp);
      if (r.data.guest?.profile_image) {
        loadProfileImage(r.data.guest.profile_image);
      }
    }).catch(() => {});
  }, []);

  const loadProfileImage = async (path) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/files/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        setProfileImageUrl(URL.createObjectURL(blob));
      }
    } catch {}
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      alert('Kun JPG, PNG eller WebP er tilladt.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      alert('Billedet må maks. være 5MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiUpload('/api/uploads/profile', formData);
      if (res.data.upload?.storage_path) {
        loadProfileImage(res.data.upload.storage_path);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Overførsel mislykkedes');
    } finally {
      setUploading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordMessage('');
    try {
      await apiPut('/api/guests/me/password', passwordForm);
      setPasswordForm({ current_password: '', new_password: '' });
      setPasswordMessage('Adgangskode opdateret.');
    } catch (err) {
      setPasswordMessage(err.response?.data?.detail || 'Kunne ikke opdatere adgangskoden.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const displayGuest = guest || user;
  const displayEmail = displayGuest?.display_email || displayGuest?.private_email || displayGuest?.corporate_email || 'Ikke angivet';
  const qrUrl = (displayGuest?._id || displayGuest?.id) ? `${API}/api/guests/${displayGuest._id || displayGuest.id}/qr` : null;
  const guestName = displayGuest?.full_name || 'Gæst';

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid="profile-page">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-50 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Min profil</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden" data-testid="guest-profile-card">
            <div className="h-20 bg-gradient-to-r from-blue-600 to-purple-600" />
            <div className="px-5 pb-5 -mt-10">
              <div className="relative mb-4">
                <div className="w-20 h-20 rounded-xl border-4 border-slate-800 overflow-hidden bg-slate-700 flex items-center justify-center">
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt="Profil" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-slate-400" />
                  )}
                </div>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center text-white transition-colors"
                  data-testid="upload-photo-btn"
                >
                  <Upload className="w-3.5 h-3.5" />
                </button>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" data-testid="upload-photo-input" />
              </div>
              <p className="text-xs text-slate-500 mb-2">JPG, PNG eller WebP. Maks. 5MB.</p>
              {uploading && <p className="text-xs text-blue-400 mb-2">Overfører...</p>}
              <h2 className="text-lg font-semibold text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>{displayGuest?.full_name}</h2>
              <p className="text-xs font-mono text-blue-400 mt-1 break-all">{displayEmail}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {displayGuest?.plus_one_allowed && <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-400 text-xs">Plus-one tilladt</span>}
                {displayGuest?.overnight_allowed && <span className="px-2 py-1 rounded bg-teal-500/10 text-teal-400 text-xs">Overnatning tilladt</span>}
                {displayGuest?.max_guests && <span className="px-2 py-1 rounded bg-slate-700/60 text-slate-300 text-xs">Maks. {displayGuest.max_guests} gæst(er)</span>}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 text-xs">
                <CheckCircle className="w-3 h-3" /> Registreret gæst
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-2 space-y-6">
          {qrUrl && (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6" data-testid="profile-qr-card">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <button type="button" onClick={() => setQrOpen(true)} className="mx-auto sm:mx-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="open-profile-qr">
                  <img src={qrUrl} alt="Event-QR" className="w-32 h-32 rounded-xl border border-slate-700 bg-white p-1" />
                </button>
                <div className="text-center sm:text-left">
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-2">
                    <QrCode className="w-4 h-4" /> Event-QR
                  </div>
                  <h3 className="text-base font-medium text-slate-100 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Dit check-in QR</h3>
                  <p className="text-sm text-slate-400">Tryk på koden for stor visning, så dørpersonalet nemt kan scanne den.</p>
                </div>
              </div>
            </div>
          )}

          {rsvp && (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6" data-testid="profile-rsvp-info">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">RSVP-status</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Deltager:</span> <span className="text-slate-200">{rsvp.attending ? 'Ja' : 'Nej'}</span></div>
                <div><span className="text-slate-500">Gæster:</span> <span className="text-slate-200">{rsvp.guest_count}</span></div>
                {rsvp.dietary_notes && <div className="col-span-2"><span className="text-slate-500">Kost:</span> <span className="text-slate-200">{rsvp.dietary_notes}</span></div>}
                {rsvp.overnight_requested && <div><span className="text-slate-500">Overnatning:</span> <span className="text-slate-200">Ønsket</span></div>}
              </div>
            </div>
          )}

          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Adgangskode</h3>
            <form onSubmit={handlePasswordChange} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Nuværende adgangskode"
                className="bg-slate-900/50 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                required
              />
              <input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Ny adgangskode"
                className="bg-slate-900/50 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100"
                minLength={6}
                required
              />
              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3">
                <Button type="submit" disabled={passwordSaving} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white">
                  {passwordSaving ? 'Gemmer...' : 'Skift adgangskode'}
                </Button>
                {passwordMessage && <span className="text-xs text-slate-400">{passwordMessage}</span>}
              </div>
            </form>
          </div>
        </div>
      </div>

      {qrOpen && qrUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4" role="dialog" aria-modal="true" data-testid="profile-qr-modal">
          <div className="w-full max-w-[min(92vw,520px)] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl border border-slate-700 bg-slate-950 p-4 sm:p-5 text-center shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="text-left">
                <h2 className="text-base font-semibold text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>Dit check-in QR</h2>
                <p className="text-sm text-slate-400">{guestName}</p>
              </div>
              <button type="button" onClick={() => setQrOpen(false)} className="min-h-10 min-w-10 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800" aria-label="Luk QR">
                <X className="w-5 h-5 mx-auto" />
              </button>
            </div>
            <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-[0_0_50px_rgba(255,255,255,0.28)]">
              <img src={qrUrl} alt="Stort check-in QR" className="w-full max-w-[min(78vw,420px)] mx-auto" />
            </div>
            <p className="mt-4 text-base font-medium text-slate-100">Vis denne QR-kode ved indgangen</p>
          </div>
        </div>
      )}
    </div>
  );
}
